// TIL1: the tile-geometry container for the "underlying tiling" overlay.
// A tiling is one base polygon (k vertices, 2D) and n affine maps that place
// it: x' = a u + b v + c, y' = d u + e v + f. Shared by the browser and the
// build tools, like format.ts.
//
// Header (64 bytes, little-endian):
//   0  char[4] 'TIL1'      4 u16 version=1      6 u16 header_bytes=64
//   8  u8 encoding (0 raw f32 affines, 1 palette + quantised translations)
//   9  u8 dims (2)        10 u8 base_count k   11 u8 flags (unused)
//  12  u32 n_tiles        16 u32 palette_count 20 u32 body_bytes
//  24  f64 origin_x       32 f64 origin_y      40 f64 scale
//  48  f32[4] bbox minx, miny, maxx, maxy (of the placed tiles)
// Body: f64[2k] base polygon; then
//   encoding 0: f32[6n] affines (a b c d e f per tile)
//   encoding 1: f32[4p] linear-part palette (a b d e); u16[n] palette index;
//               i32[n] dx; i32[n] dy   (translations quantised to the grid,
//               delta-coded in file order; the encoder sorts tiles by position)
// Substitution tilings use a handful of orientations, so the palette is small
// and the translation deltas are small integers: brotli then does the rest.

export const TIL1_MAGIC = 0x314c4954;   // 'TIL1'
export const TIL1_HEADER_BYTES = 64;
export const TIL_ENC_F32_RAW = 0;
export const TIL_ENC_PALETTE = 1;

export interface TileHeader {
  encoding: number;
  dims: number;
  baseCount: number;
  nTiles: number;
  paletteCount: number;
  bodyBytes: number;
  origin: [number, number];
  scale: number;
  bbox: { min: [number, number]; max: [number, number] };
}

export interface TilePolygons {
  kind: 'polygons';
  dims: 2;
  n: number;
  base: number[][];             // k vertices [u, v]
  affines: Float64Array;        // 6 per tile: a b c d e f
  bbox: { min: [number, number]; max: [number, number] };
}

export function parseTileHeader(buf: ArrayBuffer, byteOffset = 0): TileHeader {
  const dv = new DataView(buf, byteOffset);
  if (dv.getUint32(0, true) !== TIL1_MAGIC) throw new Error('not a TIL1 file');
  const version = dv.getUint16(4, true);
  if (version !== 1) throw new Error(`unsupported TIL1 version ${version}`);
  const headerBytes = dv.getUint16(6, true);
  if (headerBytes < TIL1_HEADER_BYTES) throw new Error('bad TIL1 header size');
  return {
    encoding: dv.getUint8(8),
    dims: dv.getUint8(9),
    baseCount: dv.getUint8(10),
    nTiles: dv.getUint32(12, true),
    paletteCount: dv.getUint32(16, true),
    bodyBytes: dv.getUint32(20, true),
    origin: [dv.getFloat64(24, true), dv.getFloat64(32, true)],
    scale: dv.getFloat64(40, true),
    bbox: { min: [dv.getFloat32(48, true), dv.getFloat32(52, true)], max: [dv.getFloat32(56, true), dv.getFloat32(60, true)] },
  };
}

export function decodeTiles(buf: ArrayBuffer, byteOffset = 0): TilePolygons {
  const h = parseTileHeader(buf, byteOffset);
  if (h.dims !== 2) throw new Error(`TIL1: unsupported dims ${h.dims}`);
  const dv = new DataView(buf, byteOffset);
  let off = TIL1_HEADER_BYTES;
  const base: number[][] = [];
  for (let i = 0; i < h.baseCount; i++) { base.push([dv.getFloat64(off, true), dv.getFloat64(off + 8, true)]); off += 16; }
  const n = h.nTiles;
  const affines = new Float64Array(n * 6);
  if (h.encoding === TIL_ENC_F32_RAW) {
    for (let i = 0; i < n * 6; i++) { affines[i] = dv.getFloat32(off, true); off += 4; }
  } else if (h.encoding === TIL_ENC_PALETTE) {
    const pal = new Float32Array(h.paletteCount * 4);
    for (let i = 0; i < pal.length; i++) { pal[i] = dv.getFloat32(off, true); off += 4; }
    const idxOff = off; off += n * 2;
    const dxOff = off; off += n * 4;
    const dyOff = off; off += n * 4;
    let qx = 0, qy = 0;
    const inv = 1 / h.scale;
    for (let i = 0; i < n; i++) {
      const k = dv.getUint16(idxOff + i * 2, true) * 4;
      qx += dv.getInt32(dxOff + i * 4, true);
      qy += dv.getInt32(dyOff + i * 4, true);
      const j = i * 6;
      affines[j] = pal[k]; affines[j + 1] = pal[k + 1]; affines[j + 2] = h.origin[0] + qx * inv;
      affines[j + 3] = pal[k + 2]; affines[j + 4] = pal[k + 3]; affines[j + 5] = h.origin[1] + qy * inv;
    }
  } else {
    throw new Error(`TIL1: unknown encoding ${h.encoding}`);
  }
  if (off - TIL1_HEADER_BYTES !== h.bodyBytes) throw new Error('TIL1: body size mismatch');
  return { kind: 'polygons', dims: 2, n, base, affines, bbox: h.bbox };
}

/** Apply tile i's affine to a base vertex. */
export function tilePoint(t: TilePolygons, i: number, v: number[]): [number, number] {
  const a = t.affines, j = i * 6;
  return [a[j] * v[0] + a[j + 1] * v[1] + a[j + 2], a[j + 3] * v[0] + a[j + 4] * v[1] + a[j + 5]];
}
